import type { TextAgent, TextNumbers } from '../api/client';

/**
 * Texts on the wallboard and TVs (1.12.32): when their numbers turn amber (red at twice), kept per
 * browser like the calls' (each screen can differ), and the words on agents' chips.
 */

export interface TextLimits {
  /** Texts waiting for an answer. */
  waiting: number;
  /** The oldest text's wait, in seconds. */
  waitSeconds: number;
}

/** Amber at 3 waiting or 5 minutes; red at 6 or 10 (a queue's reply timer, by default). */
export const DEFAULT_TEXT_LIMITS: TextLimits = { waiting: 3, waitSeconds: 300 };
const KEY = 'stitch.wallboard.textLimits';
export const TEXT_LIMIT_WAITING = [1, 2, 3, 4, 5, 8, 10];
export const TEXT_LIMIT_SECONDS = [60, 120, 180, 300, 600, 900, 1800];

const ok = (n: unknown, choices: readonly number[]): number | null => (typeof n === 'number' && choices.includes(n) ? n : null);

export function loadTextLimits(): TextLimits {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Partial<TextLimits> | null;
    return { waiting: ok(raw?.waiting, TEXT_LIMIT_WAITING) ?? DEFAULT_TEXT_LIMITS.waiting, waitSeconds: ok(raw?.waitSeconds, TEXT_LIMIT_SECONDS) ?? DEFAULT_TEXT_LIMITS.waitSeconds };
  } catch {
    return DEFAULT_TEXT_LIMITS;
  }
}

export function saveTextLimits(l: TextLimits): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(l));
  } catch {
    /* private mode: fine for this session */
  }
}

export type Tone = 'calm' | 'warn' | 'crit';
const tone = (value: number, limit: number): Tone => (value >= limit * 2 ? 'crit' : value >= limit ? 'warn' : 'calm');

/** The oldest wait now: it grows between refreshes, so the board never looks frozen. */
export function oldestNow(n: Pick<TextNumbers, 'oldestSeconds'>, since: number): number | null {
  return n.oldestSeconds === null ? null : n.oldestSeconds + since;
}

/** How the texts' numbers look: waiting and the oldest wait each, and the whole band (the worse of the two). */
export function textTones(n: Pick<TextNumbers, 'waiting' | 'oldestSeconds'>, limits: TextLimits, since: number): { waiting: Tone; oldest: Tone; band: Tone } {
  const oldest = oldestNow(n, since);
  const w = n.waiting ? tone(n.waiting, limits.waiting) : 'calm';
  const o = oldest === null ? 'calm' : tone(oldest, limits.waitSeconds);
  return { waiting: w, oldest: o, band: w === 'crit' || o === 'crit' ? 'crit' : w === 'warn' || o === 'warn' ? 'warn' : 'calm' };
}

/** "3 texts · texts paused" on an agent's chip; null when there's nothing to say. */
export function agentTextWords(a: Pick<TextAgent, 'openTexts' | 'paused'> | undefined): string | null {
  if (!a) return null;
  const parts = [a.openTexts ? `${a.openTexts} ${a.openTexts === 1 ? 'text' : 'texts'}` : '', a.paused ? 'texts paused' : ''].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}
